import { toParent } from '../space';
import { ROOT_ID, type PathPoint } from '@flobi/artwork-core';
import { styleFill, styleStroke } from '../colors';
import { KINDS } from './shapes';
import type { Point, RailButton, Tool, ToolContext } from './types';

/**
 * The arc: a quarter of an ellipse, drawn by dragging the box it fits in.
 *
 * It makes a PATH rather than a node type of its own, because that is all an arc is — two
 * anchors and the handles between them. So it needs nothing from the engine, and what it
 * leaves behind is an ordinary curve the pen and the point cursors can take apart, which
 * is what anyone drawing an arc wants next anyway.
 */

/**
 * How far a cubic's handles reach to trace a quarter ellipse.
 *
 * The number every drawing program carries: a Bézier cannot BE a circular arc, and at
 * this reach the error over a quarter turn is about one part in ten thousand — far below
 * anything a printer resolves, let alone an eye.
 */
const KAPPA = 0.5522847498307936;

/** The two anchors of an arc across a box, and the corner it closes through. */
function arcPoints(width: number, height: number, bulge: number, closed: boolean): PathPoint[] {
  const reach = KAPPA * Math.abs(bulge);
  // Which corner the curve swells towards. Positive leans on the corner the drag ends
  // beside; negative on the other one, which is the same arc turned inside out — one
  // slider covering concave and convex, since they are the same shape mirrored.
  const out: PathPoint[] =
    bulge >= 0
      ? [
          { x: 0, y: 0, kind: 'smooth', out: [reach * width, 0] },
          { x: width, y: height, kind: 'smooth', in: [width, height - reach * height] },
        ]
      : [
          { x: 0, y: 0, kind: 'smooth', out: [0, reach * height] },
          { x: width, y: height, kind: 'smooth', in: [width - reach * width, height] },
        ];
  // Closed, it is a wedge rather than a lens: the sides run back to the corner the curve
  // turns about, which is the shape the arc is a quarter OF.
  if (closed) out.push(bulge >= 0 ? { x: 0, y: height, kind: 'corner' } : { x: width, y: 0, kind: 'corner' });
  return out;
}

export function arcTool(): Tool {
  let drawing: { id: string; from: Point; size: { width: number; height: number } } | null = null;
  /** How far the curve swells, and whether it is a line or a wedge. */
  const settings = { bulge: 100, closed: false };

  const shape = (width: number, height: number) => ({
    subpaths: [{ closed: settings.closed, points: arcPoints(width, height, settings.bulge / 100, settings.closed) }],
  });

  return {
    id: 'arc',

    onPointerDown(_event, rawWorld, ctx) {
      const world = ctx.place(rawWorld);
      const style = ctx.style;
      const parent = ctx.artboardAt(world);
      const result = ctx.edit([
        {
          op: 'create',
          ref: 'arc',
          parentId: parent ?? ROOT_ID,
          node: {
            type: 'path',
            name: 'Arc',
            opacity: style.opacity,
            transform: toParent(ctx.scene, parent, [1, 0, 0, 1, world.x, world.y]),
            path: shape(1, 1),
            // An open arc has no inside to fill, so it draws as a line whatever the rail
            // says; closed, it takes the fill like any other shape.
            fills: settings.closed && style.fillOn ? [{ type: 'solid', color: styleFill(style) }] : [],
            // And only the open one is forced to have a stroke, since a line with none is
            // nothing at all. A closed wedge follows Stroke like a rectangle does: with
            // Stroke at None it always came out with a thin dark outline anyway
            // (QA 2026-09-25, arc-width-warp-F18).
            ...(!settings.closed || style.strokeWeight > 0
              ? {
                  strokes: [{ type: 'solid', color: styleStroke(style) }],
                  strokeStyle: { weight: settings.closed ? style.strokeWeight : Math.max(1, style.strokeWeight) },
                }
              : {}),
          },
        },
      ]);
      if (!result.ok) return false;
      drawing = { id: result.created.arc!, from: world, size: { width: 1, height: 1 } };
      ctx.setSelection([drawing.id]);
      return true;
    },

    onPointerMove(event, rawWorld, ctx) {
      if (!drawing || event.buttons === 0) return;
      const world = ctx.place(rawWorld, [drawing.id]);
      let width = world.x - drawing.from.x;
      let height = world.y - drawing.from.y;
      // Shift holds it to a circle's quarter rather than an ellipse's, keeping the sign
      // so the arc still turns whichever way the drag went.
      if (event.shiftKey) {
        const square = Math.max(Math.abs(width), Math.abs(height));
        width = Math.sign(width || 1) * square;
        height = Math.sign(height || 1) * square;
      }
      ctx.edit([{ op: 'set', id: drawing.id, props: { path: shape(width, height) } }]);
      drawing.size = { width, height };
    },

    onPointerUp(_event, _world, ctx) {
      if (!drawing) return;
      // A drag that came back to where it started leaves an arc with no size: nothing to
      // see, but a layer all the same. It goes, in the same step it was made in
      // (QA 2026-09-25, arc-width-warp-F19).
      const { id, size } = drawing;
      if (Math.abs(size.width) < 1 && Math.abs(size.height) < 1) ctx.edit([{ op: 'delete', id }]);
      drawing = null;
      ctx.stopCapturing();
      ctx.finish();
    },

    deactivate() {
      drawing = null;
    },

    /**
     * The kinds above, then how the arc bends and whether it closes.
     *
     * The same five-and-one list every shape shows, because the arc belongs to that family
     * however differently it is built underneath — what a tool is made of is not something
     * the rail should make anyone think about.
     */
    rail(ctx: ToolContext) {
      const kinds: RailButton[] = KINDS.map((item) => ({
        id: item.id,
        icon: item.icon,
        title: item.title,
        pressed: item.id === 'arc',
        run: () => ctx.chooseTool(item.id),
      }));
      const change = (patch: Partial<typeof settings>) => {
        Object.assign(settings, patch);
        ctx.railChanged();
      };
      return {
        top: { kind: 'buttons' as const, items: kinds },
        middle: {
          kind: 'slider' as const,
          icon: 'corner' as const,
          title: 'Bend — how far the curve leans away from a straight line',
          value: settings.bulge,
          min: -100,
          max: 100,
          format: (value: number) => `${value}%`,
          onChange: (bulge: number) => change({ bulge }),
        },
        bottom: {
          kind: 'buttons' as const,
          items: [
            {
              id: 'open',
              icon: 'line' as const,
              title: 'Open — an arc with two loose ends',
              pressed: !settings.closed,
              run: () => change({ closed: false }),
            },
            {
              id: 'closed',
              icon: 'shape' as const,
              title: 'Closed — shut back to the corner, so it can be filled',
              pressed: settings.closed,
              run: () => change({ closed: true }),
            },
          ],
        },
        style: true,
      };
    },
  };
}
